import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbCalendarEvent, TbMapPin, TbUsers } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { type CalendarEventsPayload, parseCalendarEventsPayload } from "./calendarEventsSchema";
import { formatDay, formatTime, isSameDay } from "./calendarRenderUtils";

interface CalendarEventsRendererProps {
  block: StreamingBlock;
}

export const CalendarEventsRenderer = (props: CalendarEventsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<CalendarEventsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseCalendarEventsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("calendar_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().events.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbCalendarEvent class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{rt.t("calendar_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().events.length === 1
                    ? rt.t("calendar_card.results_one")
                    : rt.t("calendar_card.results", { count: String(data().events.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().events}>
                  {(event) => (
                    <li>
                      <a
                        href={event.htmlLink ?? "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="block px-3 py-2 transition-colors hover:bg-panel-hover"
                        classList={{ "pointer-events-none": !event.htmlLink }}
                      >
                        <div class="flex items-baseline justify-between gap-3">
                          <h3 class="min-w-0 truncate text-sm font-bold text-primary-text">
                            {event.title || rt.t("calendar_card.untitled")}
                          </h3>
                          <span class="shrink-0 text-xs text-muted-text">
                            {event.allDay
                              ? `${formatDay(event.start)} · ${rt.t("calendar_card.all_day")}`
                              : isSameDay(event.start, event.end)
                                ? `${formatDay(event.start)} · ${formatTime(event.start)}–${formatTime(event.end)}`
                                : `${formatDay(event.start)} ${formatTime(event.start)} – ${formatDay(event.end)} ${formatTime(event.end)}`}
                          </span>
                        </div>
                        <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-text">
                          <Show when={event.location}>
                            <span class="inline-flex min-w-0 items-center gap-1">
                              <TbMapPin class="size-3.5 shrink-0" />
                              <span class="truncate">{event.location}</span>
                            </span>
                          </Show>
                          <Show when={event.attendees && event.attendees.length > 0}>
                            <span class="inline-flex shrink-0 items-center gap-1">
                              <TbUsers class="size-3.5 shrink-0" />
                              {rt.t("calendar_card.attendees", { count: String(event.attendees?.length ?? 0) })}
                            </span>
                          </Show>
                        </div>
                      </a>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
